import { DeliveryType } from "./delivery";
import { ValueOf } from "./utils";
import { InsuranceID } from "../models";

/**
 * For reporting errors to Google Analytics so that we can see
 * what the user was trying to do when an error has occurred.
 *
 * TODO Use directly REDUX action names so that we can get rid of this useless mapping (and the cost of maintaining it)
 */
export const CLIENT_EVENT_TYPES = {
    CREDIT_CHECK_BUSINESS: "creditCheckBusiness",
    ENTER_ZIP_CODE: "enterZipCode",
    CHOOSE_MBB_NUMBER: "chooseMobileBroadbandNumber",
    GET_AVAILABLE_NUMBERS: "getAvailableNumbers",
    SET_PHONE_NUMBER: "setPhoneNumber",
    ADD_BUNDLE_TO_CART: "addBundleToCart",
    CHECK_ORGNUMBER: "checkOrganisationNumber",
    GO_TO_PAYMENT: "goToPayment",
    WEBSHOP_WARNING: "webshopWarning",
};
export type ClientEventType = keyof typeof CLIENT_EVENT_TYPES;
export type ClientEventTypeValue = ValueOf<typeof CLIENT_EVENT_TYPES>;

export const GA_EVENTS = {
    ADD_TO_CART: "addToCart",
    REMOVE_FROM_CART: "removeFromCart",
    CHECKOUT: "checkout",
    TRANSACTION: "transaction",
    FUNNEL: "funnel_event",
    UI_CLICK: "click",
    SHOP_ERROR: "shopError",
    AUTH: "authentication",
    WEBSHOP_CLICK: "webshop_click",
} as const;

export const GA_TEXT_CONSTS = {
    CUSTOMER_TYPE: {
        NEW: "new",
        EXISTING: "existing",
        PORT_IN: "port_in",
    },
    SALES_TYPE: {
        NEW_NUMBER: "New number",
        EXISTING: "Existing",
        PORT_IN: "Portin",
        UP_SELL: "Upsell",
        PROLONGING: "Prolonging",
        DOWN_SELL: "Downsell",
        ACQUISITION: "Acquisition",
        RETENTION: "Retention",
    },
    FINANCING_TYPE: {
        ADYEN_CC: "Adyen Credit Card",
        BILLING: "Billing",
        UPFRONT: "Upfront",
        NETS: "Nets",
        NETS_3D: "Nets 3D Secure",
        VIPPS: "Vipps",
        VIPPS_v2: "Vipps",
        INVOICE: "Firstbill",
        SWITCH: "SVITSJ",
        RESWITCH: "Re-SVITSJ",
        LEASE: "Lease",
        TELIA_FINANCING: "Telia Financing",
        VOUCHER: "Gavekort",
        FLEX_LEASE: "Flexlease",
    } as const,
    SUBSCRIPTION_TYPE: {
        SWITCH: "SVITSJ",
        RESWITCH: "Re-SVITSJ",
        COMMITMENT: "Commitment",
        SIM_DEAL: "SIM deal",
        SIM_ONLY: "SIM only",
        RINGTIME: "Ringtime",
        MBB: "MBB",
        SERVICE: "SERVICE",
        TERMINAL: "Terminal",
    },
    PRODUCT: {
        SIM: "SIM",
        NEW_NUMBER: "New number",
    },
    LISTS: {
        HANDSET: "Mobile phone list",
        TABLET: "Tablet list",
        WEARABLE: "Smart Watch list",
        CONNECTED_DEVICE: "Smart Home list",
        MODEM: "Router list",
        ACCESSORY: "Accessory list",
        SIM_DEAL: "SIM deal list",
        SUBSCRIPTION: "Mobile subscription list",
    },
    CATEGORY_SUBTYPES: {
        HEADSET: "Headset",
        SPEAKER: "Speaker",
        COVER: "Cover",
        CHARGER: "Charger",
        STORAGE: "Storage",
        SCREENPROTECTOR: "Screenprotector",
        SMARTWATCHES: "Smart watch",
        OTHER: "Other",
    },
    EVENTS: GA_EVENTS,
    AFFILIATION: "Telia Nettbutikk B2C",
    AFFILIATION_B2B: "Telia Bedrift Shop",
    AFFILIATION_MINSIDE: "Telia Min Side",
    AFFILIATION_APP: "Telia App",
    BRAND: "Telia",
    NOK: "NOK",
} as const;

export const INSURANCE: { [key in InsuranceID]: { MULTIPLIER: number; NAME: string } } = {
    "INSURANCE_LL+INSURNDFS": {
        NAME: "Forsikring Large",
        MULTIPLIER: 12,
    },
    "INSURANCE_MM+INSURNDES": {
        NAME: "Forsikring Medium",
        MULTIPLIER: 12,
    },
    "INSURANCE_SS+INSURNDDS": {
        NAME: "Forsikring Small",
        MULTIPLIER: 12,
    },
    "INSURANCE_XLL+INSURNDGS": {
        NAME: "Forsikring X-Large",
        MULTIPLIER: 12,
    },
    "INSURANCE_XXLL_PLUS+INSURNDIS": {
        NAME: "Forsikring XX-Large",
        MULTIPLIER: 12,
    },
    INSURANCE_SWITCH: {
        NAME: "Svitsj Plussforsikring",
        MULTIPLIER: 12,
    },
    "INSURANCE_SWITCH_4+INSURLS4V": {
        NAME: "Svitsj Plussforsikring 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_SWITCH_4+INSURLS4U": {
        NAME: "Svitsj Plussforsikring 24 md. kostnadsfritt",
        MULTIPLIER: 24,
    },
    "INSURANCE_PLUS_S+INSURNDOP": {
        NAME: "Plussforsikring S 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_PLUS_M+INSURNDPP": {
        NAME: "Plussforsikring M 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_PLUS_L+INSURNDQP": {
        NAME: "Plussforsikring L 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_PLUS_S_PREOWNED+INSURNE4S": {
        NAME: "LikeFin Plussforsikring S 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_PLUS_M_PREOWNED+INSURNE5S": {
        NAME: "LikeFin Plussforsikring M 2 md. gratis",
        MULTIPLIER: 10,
    },
    "INSURANCE_PLUS_L_PREOWNED+INSURNE7S": {
        NAME: "LikeFin Plussforsikring L 2 md. gratis",
        MULTIPLIER: 10,
    },
} as const;

export const GA_CATEGORIES = {
    SIM: "Service/SIM Card",
    NEW_NUMBER: "Service/Fee",
    BREAKAGE_FEE: "Service/Fee",
    SWITCH_BREAKAGE_FEE: "Service/Fee",
    SWITCH_REMAINING_COST: "Service/Fee",
    HANDSET: "Hardware/Telephone/Mobile",
    "HANDSET-PREOWNED": "Hardware/Telephone/Mobile",
    MODEM: "Hardware/Router/Mobile",
    MODEM_WIFIX: "Hardware/Router/Extender",
    TABLET: "Hardware/Tablet",
    WEARABLE: "Hardware/Smart watch",
    CONNECTED_DEVICE: "Hardware/Smarthome",
    SUBSCRIPTION_PREPAID: "Prepaid/Telephone/Mobile",
    SUBSCRIPTION_PREPAID_REFILL: "Prepaid-refill/Telephone/Mobile",
    SUBSCRIPTION_POSTPAID: "Subscription/Telephone/Mobile",
    SUBSCRIPTION_MBB: "Subscription/Broadband/Mobile",
    SUBSCRIPTION_FWA: "Subscription/Broadband/Fixed wireless",
    SUBSCRIPTION_FWA_TV: "Subscription/Broadband/Fixed wireless/TV",
    SUBSCRIPTION_FWA_CABIN: "Subscription/Broadband/Fixed/Cabin wireless",
    SUBSCRIPTION_FWA_CABIN_TV: "Subscription/Broadband/Fixed/Cabin wireless/TV",
    SERVICE_INSURANCE: "Service/Insurance",
    SERVICE_OTHER: "Service/Other",
    SERVICE_VAS: "Service/VAS",
    DELIVERY: "Service/Delivery",
    SUBSCRIPTION_SECURITY_TRYGG: "Subscription/Security/Trygg",
} as const;

export type CategoriesString = ValueOf<typeof GA_CATEGORIES> | `Hardware/Accessory/${string}`;

export const DELIVERY: { [type in DeliveryType]: string } = {
    CARRYON_POST: "Levert til nærmeste utleveringsted (PostNord)",
    CARRYON_POST_BRING: "Levert til nærmeste utleveringsted (Bring)",
    CARRYON_EXP: "Jetpak",
    CARRYON_EXP_JUL: "Jetpak-jul",
    CARRYON_DOOR: "Levert på døren",
    PICK_UP_IN_SHOP: "Klikk og Hent",
    PICK_UP_POINT: "Levert til valgfritt utleveringsted (PostNord)",
    PICK_UP_POINT_BRING: "Levert til valgfritt utleveringsted (Bring)",
    LETTER: "Brevpost",
    BUSN_PRC: "Bedriftspakke",
    PROGRESS_BAR: "Levering",
} as const;
export const ACCESSORY_CATEGORIES = (category: string): CategoriesString => `Hardware/Accessory/${category}`;

export const GTM_EVENT = {
    IMPRESSION: "productListing", // show product
    PRODUCT_CLICK: "productClick",
    ADD_TO_CART: "addToCart", // add product to shopping cart
    DELIVERY: "DELIVERY",
    TRANSACTION: "transaction", // buy all the products in shopping cart
} as const;

export const GTM_ECOMMERCE_STEP = {
    PERSONAL_DETAILS: 1, // before bankid
    LEGAL: 2, // after bankid
    MEMBER: 3,
    SUMMARY: 4,
} as const;

export const GTM_ECOMMERCE_REFILL_STEP = {
    SUMMARY: 3,
} as const;
